import type {ChipProps} from '@mui/material/Chip';
import type {TooltipProps} from '@mui/material/Tooltip';
import type {SxProps, Theme} from '@mui/material/styles';
import type {SystemStyleObject} from '@mui/system';

export const WP_STATUS = {
    NOT_STARTED: 0,
    INCOMPLETE: 1,
    HAS_ISSUE: 2,
    COMPLETED: 3,
} as const;

export type WpStatusValue = typeof WP_STATUS[keyof typeof WP_STATUS];

export const WP_STATUS_LABELS: Record<number, string> = {
    [WP_STATUS.NOT_STARTED]: 'Not Started',
    [WP_STATUS.INCOMPLETE]: 'Processing',
    [WP_STATUS.HAS_ISSUE]: 'Issue',
    [WP_STATUS.COMPLETED]: 'Completed',
};

export function shouldColorScheduleChip(item: {
    wpStatus?: number | null;
    openingAmount?: number | null;
    outstanding?: boolean | null;
}): boolean {
    const opening = Number(item?.openingAmount ?? 0) || 0;
    const status = item?.wpStatus;
    return opening !== 0 || status != null || Boolean(item?.outstanding);
}

const ADJUSTMENT_CHIP_SX = {
    height: 18,
    boxSizing: 'border-box',
    '& .MuiChip-label': {px: 1, py: 0, lineHeight: 1.2},
} as const;

export function getWpStatusChipProps(wpStatus?: number | null): Pick<ChipProps, 'variant' | 'color' | 'sx'> {
    switch (wpStatus) {
        case WP_STATUS.NOT_STARTED:
            return {
                variant: 'filled',
                color: 'default',
                sx: {
                    bgcolor: '#FFFFFF',
                    border: '1px solid #BDBDBD',
                    color: 'text.primary',
                },
            };
        case WP_STATUS.INCOMPLETE:
            return {
                variant: 'filled',
                color: 'default',
                sx: {bgcolor: '#FFEB3B', color: '#5D4037'},
            };
        case WP_STATUS.HAS_ISSUE:
            return {variant: 'filled', color: 'error'};
        case WP_STATUS.COMPLETED:
            return {variant: 'filled', color: 'success'};
        default:
            return {variant: 'outlined', color: 'primary'};
    }
}

export function getWpStatusTooltip(
    wpStatus?: number | null,
    wpIssueRemark?: string | null,
    outstanding?: boolean | null,
    outstandingRemark?: string | null,
): string | undefined {
    const parts: string[] = [];

    if (wpStatus === WP_STATUS.HAS_ISSUE) {
        const label = WP_STATUS_LABELS[WP_STATUS.HAS_ISSUE];
        const trimmed = (wpIssueRemark || '').trim();
        if (trimmed) {
            parts.push(trimmed.includes('\n') ? `${label}:\n${trimmed}` : `${label}: ${trimmed}`);
        } else {
            parts.push(label);
        }
    } else if (wpStatus != null && WP_STATUS_LABELS[wpStatus]) {
        // Non-issue statuses only contribute a label when there is no outstanding tooltip line
        if (!outstanding) {
            parts.push(WP_STATUS_LABELS[wpStatus]);
        }
    }

    if (outstanding) {
        const trimmed = (outstandingRemark || '').trim();
        if (trimmed) {
            parts.push(trimmed.includes('\n') ? `Outstanding:\n${trimmed}` : `Outstanding: ${trimmed}`);
        } else {
            parts.push('Outstanding');
        }
    }

    if (parts.length === 0) return undefined;
    return parts.join('\n');
}

/** @deprecated Issue mark control is always shown; visibility is no longer gated by check status. */
export function shouldShowIssueMarkControl(
    checksPassed: boolean,
    initialWpStatus?: number | null,
): boolean {
    return !checksPassed
        || initialWpStatus === WP_STATUS.HAS_ISSUE
        || initialWpStatus === WP_STATUS.COMPLETED;
}

export type ScheduleChipItem = {
    wpStatus?: number | null;
    wpIssueRemark?: string | null;
    openingAmount?: number | null;
    outstanding?: boolean | null;
    outstandingRemark?: string | null;
};

const DEFAULT_SCHEDULE_CHIP_PROPS: Pick<ChipProps, 'variant' | 'color'> = {
    variant: 'outlined',
    color: 'primary',
};

function resolveChipSx(statusSx: ChipProps['sx'] | undefined): ChipProps['sx'] {
    return (theme: Theme): SystemStyleObject<Theme> => {
        const resolvedStatusSx: SystemStyleObject<Theme> =
            typeof statusSx === 'function'
                ? (statusSx(theme) as SystemStyleObject<Theme>)
                : ((statusSx ?? {}) as SystemStyleObject<Theme>);
        const merged: SystemStyleObject<Theme> = {
            ...ADJUSTMENT_CHIP_SX,
            ...resolvedStatusSx,
        };
        if (theme.palette.mode !== 'dark') {
            return merged;
        }
        const black = theme.palette.common.black;
        const statusLabelSx = (resolvedStatusSx as Record<string, unknown>)['& .MuiChip-label'] as
            SystemStyleObject<Theme> | undefined;
        return {
            ...merged,
            color: black,
            '& .MuiChip-label': {
                ...(ADJUSTMENT_CHIP_SX['& .MuiChip-label'] as SystemStyleObject<Theme>),
                ...(statusLabelSx ?? {}),
                color: black,
            },
        };
    };
}

/** adjustment 模式下 wp_status = 2 时 chip tooltip 的容器样式 */
export const WP_STATUS_ISSUE_TOOLTIP_SLOT_PROPS: TooltipProps['slotProps'] = {
    tooltip: {
        sx: {
            bgcolor: 'error.main',
            color: 'common.white',
            fontSize: '0.8em',
            border: 1,
            borderColor: 'error.dark',
            maxWidth: 400,
            p: 1,
        },
    },
};

/** adjustment 模式下 wp_status = 2 时 chip tooltip 内容区样式 */
export const WP_STATUS_ISSUE_TOOLTIP_CONTENT_SX: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'flex-start',
    gap: 0.75,
    color: 'common.white',
    fontSize: '0.8em',
    lineHeight: 1.4,
};

/** adjustment 模式下 wp_status = 2 时 chip tooltip 文本区样式（支持多行 wpIssueRemark） */
export const WP_STATUS_ISSUE_TOOLTIP_TEXT_SX: SxProps<Theme> = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: 1.4,
    flex: 1,
    minWidth: 0,
};

/** adjustment 模式下 wp_status = 2 时 chip tooltip 警告图标样式 */
export const WP_STATUS_ISSUE_TOOLTIP_ICON_SX: SxProps<Theme> = {
    fontSize: '0.8em',
    color: 'common.white',
    mt: '0.1em',
    flexShrink: 0,
};

/** adjustment 模式下 wp_status = Issue 或 Outstanding 时在 hover 后加载 chip tooltip */
export function shouldLoadScheduleChipTooltip(
    mode: 'fs' | 'adjustment' | string | undefined,
    wpStatus?: number | null,
    outstanding?: boolean | null,
): boolean {
    return mode === 'adjustment'
        && (wpStatus === WP_STATUS.HAS_ISSUE || Boolean(outstanding));
}

/** Resolve scheduleName Chip props for FsAdjustment (adjustment mode) vs default FS view. */
export function resolveScheduleChipProps(
    mode: 'fs' | 'adjustment' | string | undefined,
    item: ScheduleChipItem,
): Pick<ChipProps, 'variant' | 'color' | 'sx'> {
    if (mode !== 'adjustment' || !shouldColorScheduleChip(item)) {
        return DEFAULT_SCHEDULE_CHIP_PROPS;
    }
    // Outstanding (and Issue) → red; otherwise follow wp_status colors
    const statusProps = Boolean(item.outstanding)
        ? getWpStatusChipProps(WP_STATUS.HAS_ISSUE)
        : getWpStatusChipProps(item.wpStatus);
    return {
        ...statusProps,
        sx: resolveChipSx(statusProps.sx),
    };
}
